import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Lead each card with its second image in the default view. Started eagerly
    // (not in idle) and self-retries per frame so the swap lands as soon as each
    // card's slideshow is interactive — keeping the flash to a frame or two.
    this.#syncCardLeadImage();

    // Apply the CLP module filter for the initial view once all modules have
    // initialised — also needed for the default view, where catalogue-only
    // modules must be hidden, and after a view restored from sessionStorage.
    requestIdleCallback(() => {
      this.#applyClpViewFilter(this.refs.grid?.getAttribute('product-grid-view') ?? 'default');
    });
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Instant scroll so the viewport is at the grid top before the view
    // transition measures which cards are visible (getCardsToAnimate).
    // Smooth scroll from a bottom position would leave most cards hidden
    // during the transition (content-visibility: hidden).
    grid.scrollIntoView({ behavior: 'instant', block: 'start' });

    // The programmatic scroll above doesn't always trigger the header's own
    // scroll-direction detector (rAF timing). Sync the header state directly
    // so it hides when the page has scrolled past the top — the same state
    // header.js would set if it detected the downward scroll correctly.
    if (window.scrollY > 0) {
      const headerComp = /** @type {HTMLElement | null} */ (document.querySelector('header-component'));
      if (headerComp?.getAttribute('sticky') === 'always') {
        headerComp.dataset.stickyState = 'active';
        headerComp.dataset.scrollDirection = 'down';
      }
    }

    await startViewTransition(() => {
      this.#setLayout(value);
      this.#applyClpViewFilter(value);
      this.#syncCardLeadImage(value);
    }, ['product-grid']);

    // Column count changes between views (4-col default ↔ 3-col catalogue) but
    // no resize event fires. Re-place each CLP so it lands after a full row.
    this.querySelectorAll('clp-content-spot').forEach((el) => el.placeInGrid?.());

    // Re-sync radio inputs to the actual grid attribute. Rapid clicks can
    // start concurrent transitions; the last one wins the grid state but an
    // earlier click may have already checked a different input. Without this
    // sync the inputs and grid diverge, and clicking the "stuck" input fires
    // no change event because it is already checked.
    const finalView = grid.getAttribute('product-grid-view');
    if (finalView) {
      this.querySelectorAll(
        'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
      ).forEach((input) => {
        input.checked = input.value === finalView;
      });
    }

    this.resetReveal();

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, finalView ?? value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
    this.#applyClpViewFilter('default');
    this.#syncCardLeadImage('default');
    this.querySelectorAll('clp-content-spot').forEach((el) => el.placeInGrid?.());
  };

  /**
   * Show or hide CLP merchandising modules to match the active grid view.
   *
   * Each module carries the editor's view choice as `data-mlp-chapter-content`
   * keys ('default' and/or 'catalog'); no attribute means "Both". The dense
   * zoom-out (archive) view shows no modules at all.
   *
   * @param {string} view - The grid view ('default' | 'catalogue' | 'zoom-out')
   */
  #applyClpViewFilter(view) {
    const viewKey = { default: 'default', catalogue: 'catalog' }[view] ?? null;

    this.querySelectorAll('.clp-content-spot').forEach((el) => {
      const attr = el.getAttribute('data-mlp-chapter-content');
      const keys = (attr || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
      /** @type {HTMLElement} */ (el).hidden = viewKey === null || (keys.length > 0 && !keys.includes(viewKey));
    });
  }

  /**
   * Renders the next page, then re-applies the default-view lead image so newly
   * appended cards match the active view (see {@link ResultsList#syncCardLeadImage}).
   *
   * @param {Event} event
   */
  async loadNextPage(event) {
    await super.loadNextPage(event);
    this.#syncCardLeadImage();
  }

  /**
   * Lead each product card with its second image in the default grid view.
   *
   * GANT requirement: in the default (middle) grid view, product cards rest on
   * their second media (the styled / model shot) instead of the featured image.
   * The catalogue and zoom-out views keep the featured image first.
   *
   * @param {string} [view] - The active grid view; read from the grid when omitted.
   */
  #syncCardLeadImage(view = this.refs.grid?.getAttribute('product-grid-view') ?? 'default') {
    const { grid } = this.refs;
    if (!grid) return;

    const targetIndex = view === 'default' ? 1 : 0;

    grid.querySelectorAll('product-card slideshow-component').forEach((node) => {
      this.#leadCardSlide(/** @type {any} */ (node), targetIndex);
    });
  }

  /**
   * Selects `targetIndex` on a card's slideshow without animation. `select` is a
   * no-op until the slideshow finishes setup (its scroller is created), so this
   * retries on the next frames — bounded — until the slide lands. This bridges
   * the gap on initial load and for cards just appended by pagination.
   *
   * @param {any} slideshow - The card's `slideshow-component`.
   * @param {number} targetIndex - The slide index to rest on.
   * @param {number} [attempt] - Internal retry counter.
   */
  #leadCardSlide(slideshow, targetIndex, attempt = 0) {
    if (typeof slideshow.select !== 'function') return;

    // Nothing to do when the card has no slide at the requested index.
    if ((slideshow.slides?.length ?? 0) <= targetIndex) return;

    slideshow.select(targetIndex, undefined, { animate: false });

    // `select` returned early (slideshow not ready yet): try again next frame.
    if (slideshow.current !== targetIndex && attempt < 20) {
      requestAnimationFrame(() => this.#leadCardSlide(slideshow, targetIndex, attempt + 1));
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
